import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SaveIcon from "@mui/icons-material/Save";
import React, { useEffect, useMemo, useState } from "react";
import Topbar from "../../../../../components/topbar/TopBar";
import { TarifaClientCityCreate } from "../../city/page/TarifaClientCityCreate";
import { getClientCityListUseCase } from "../../city/domain/usecases/getClientCityListUseCase";
import { createClientCityToCityUseCase } from "../domain/usecases/createClientCityToCityUseCase";
import { deleteClientCityToCityUseCase } from "../domain/usecases/deleteClientCityToCityUseCase";
import { getClientCityToCityByIdUseCase } from "../domain/usecases/getClientCityToCityByIdUseCase";
import { getClientCityToCityListUseCase } from "../domain/usecases/getClientCityToCityListUseCase";
import { updateClientCityToCityUseCase } from "../domain/usecases/updateClientCityToCityUseCase";

const emptyLocation = {
  id: null,
  name: "",
  latitude: null,
  longitude: null,
};

function createEmptyForm() {
  return {
    origin: { ...emptyLocation },
    destination: { ...emptyLocation },
    price: "",
    priceKm: "",
  };
}

function createPayload(form) {
  return {
    origin_id: Number(form.origin.id),
    destination_id: Number(form.destination.id),
    price: Number(form.price),
    priceKm: Number(form.priceKm),
  };
}

function normalizeCoordinate(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
}

function getFirstDefinedValue(...values) {
  return values.find((value) => value !== null && value !== undefined);
}

function createFormFromRecord(record) {
  if (!record) {
    return createEmptyForm();
  }

  return {
    origin: {
      id: record.origin_id ?? record.originId ?? null,
      name: record.origin_name || record.origin || "",
      latitude: normalizeCoordinate(
        getFirstDefinedValue(record.origin_lat, record.originLat),
      ),
      longitude: normalizeCoordinate(
        getFirstDefinedValue(record.origin_lng, record.originLng),
      ),
    },
    destination: {
      id: record.destination_id ?? record.destinationId ?? null,
      name: record.destination_name || record.destination || "",
      latitude: normalizeCoordinate(
        getFirstDefinedValue(record.destination_lat, record.destinationLat),
      ),
      longitude: normalizeCoordinate(
        getFirstDefinedValue(record.destination_lng, record.destinationLng),
      ),
    },
    price:
      record.price === null || record.price === undefined
        ? ""
        : String(record.price),
    priceKm:
      record.priceKm === null || record.priceKm === undefined
        ? ""
        : String(record.priceKm),
  };
}

function isFormReady(form) {
  return (
    Number.isInteger(Number(form.origin.id)) &&
    Number(form.origin.id) > 0 &&
    form.origin.name.trim() &&
    typeof form.origin.latitude === "number" &&
    !Number.isNaN(form.origin.latitude) &&
    typeof form.origin.longitude === "number" &&
    !Number.isNaN(form.origin.longitude) &&
    Number.isInteger(Number(form.destination.id)) &&
    Number(form.destination.id) > 0 &&
    form.destination.name.trim() &&
    typeof form.destination.latitude === "number" &&
    !Number.isNaN(form.destination.latitude) &&
    typeof form.destination.longitude === "number" &&
    !Number.isNaN(form.destination.longitude) &&
    form.price !== "" &&
    !Number.isNaN(Number(form.price)) &&
    Number(form.price) >= 0 &&
    form.priceKm !== "" &&
    !Number.isNaN(Number(form.priceKm)) &&
    Number(form.priceKm) >= 0
  );
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return response?.success === true || message.includes("success");
}

function formatPrice(price) {
  return Number(price).toFixed(2);
}

function getRecordOriginName(record) {
  return record.origin_name || record.origin || "";
}

function getRecordDestinationName(record) {
  return record.destination_name || record.destination || "";
}

function createLocationFromClientCity(city) {
  if (!city) {
    return { ...emptyLocation };
  }

  return {
    id: city.id,
    name: city.name || "",
    latitude: normalizeCoordinate(city.latitude),
    longitude: normalizeCoordinate(city.longitude),
  };
}

function isSameCity(option, value) {
  if (!option || !value) {
    return false;
  }

  if (option.id !== undefined && value.id !== undefined) {
    return Number(option.id) === Number(value.id);
  }

  return (
    option.name === value.name &&
    Number(option.latitude) === Number(value.latitude) &&
    Number(option.longitude) === Number(value.longitude)
  );
}

function CitySelect({ label, value, options, loading, onChange }) {
  const selectedOption = value?.name ? value : null;
  const mergedOptions = useMemo(() => {
    if (!selectedOption) {
      return options;
    }

    const exists = options.some((option) => isSameCity(option, selectedOption));
    return exists ? options : [selectedOption, ...options];
  }, [options, selectedOption]);

  return (
    <Autocomplete
      fullWidth
      loading={loading}
      options={mergedOptions}
      value={selectedOption}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={isSameCity}
      onChange={(event, city) => onChange(createLocationFromClientCity(city))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={loading ? "Cargando ciudades..." : label}
        />
      )}
    />
  );
}

function TarifaClientCityForm({
  form,
  onChange,
  onSubmit,
  submitLabel,
  loadingLabel,
  cityOptions = [],
  loadingCityOptions = false,
  disabled = false,
}) {
  const canSubmit = useMemo(() => isFormReady(form), [form]);

  return (
    <Stack spacing={2}>
      <CitySelect
        label="Origen"
        value={form.origin}
        options={cityOptions}
        loading={loadingCityOptions}
        onChange={(origin) => onChange({ ...form, origin })}
      />
      <CitySelect
        label="Destino"
        value={form.destination}
        options={cityOptions}
        loading={loadingCityOptions}
        onChange={(destination) => onChange({ ...form, destination })}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          fullWidth
          type="number"
          label="Precio"
          value={form.price}
          onChange={(event) =>
            onChange({ ...form, price: event.target.value })
          }
          inputProps={{ min: "0", step: "0.01" }}
        />
        <TextField
          fullWidth
          type="number"
          label="Precio por kilometro"
          value={form.priceKm}
          onChange={(event) =>
            onChange({ ...form, priceKm: event.target.value })
          }
          inputProps={{ min: "0", step: "0.01" }}
        />
      </Stack>
      <Button
        fullWidth
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={onSubmit}
        disabled={!canSubmit || disabled}
        sx={{
          mt: 1,
          minHeight: 50,
          borderRadius: 2,
          textTransform: "uppercase",
          fontWeight: 700,
          bgcolor: "#165c53",
          color: "#fff",
          "&:hover": {
            bgcolor: "#12483f",
          },
        }}
      >
        {(disabled ? loadingLabel : submitLabel).toUpperCase()}
      </Button>
    </Stack>
  );
}

export const TarifaClientCityCreatePage = () => {
  const [form, setForm] = useState(createEmptyForm());
  const [records, setRecords] = useState([]);
  const [clientCities, setClientCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClientCities, setLoadingClientCities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(createEmptyForm());
  const [cityCreateOpen, setCityCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const loadRatesCity = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const rates = await getClientCityToCityListUseCase();
      setRecords(rates);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo cargar la lista de tarifas.",
      });
      setRecords([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadClientCities = async () => {
    try {
      setLoadingClientCities(true);
      const cities = await getClientCityListUseCase();
      setClientCities(cities.map(createLocationFromClientCity));
    } catch (error) {
      setClientCities([]);
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo cargar la lista de ciudades.",
      });
    } finally {
      setLoadingClientCities(false);
    }
  };

  useEffect(() => {
    loadRatesCity();
    loadClientCities();
  }, []);

  const handleCreate = async () => {
    const payload = createPayload(form);

    try {
      setSaving(true);
      const response = await createClientCityToCityUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear la tarifa de ciudad a ciudad.");
      }

      setForm(createEmptyForm());
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa de ciudad a ciudad creada correctamente.",
      });
      await loadRatesCity({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text:
          error.message || "No se pudo crear la tarifa de ciudad a ciudad.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMenu = (event, record) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleOpenEdit = async () => {
    try {
      const record = await getClientCityToCityByIdUseCase(selectedRecord.id);
      setSelectedRecord(record);
      setEditForm(createFormFromRecord(record));
      setEditOpen(true);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo leer la tarifa seleccionada.",
      });
    } finally {
      handleCloseMenu();
    }
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setSelectedRecord(null);
    setEditForm(createEmptyForm());
  };

  const handleUpdate = async () => {
    const payload = {
      id: selectedRecord.id,
      ...createPayload(editForm),
    };

    try {
      setSaving(true);
      const response = await updateClientCityToCityUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo actualizar la tarifa de ciudad a ciudad.");
      }

      handleCloseEdit();
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa de ciudad a ciudad actualizada correctamente.",
      });
      await loadRatesCity({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text:
          error.message ||
          "No se pudo actualizar la tarifa de ciudad a ciudad.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = () => {
    setDeleteOpen(true);
    handleCloseMenu();
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedRecord(null);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await deleteClientCityToCityUseCase(selectedRecord.id);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo eliminar la tarifa de ciudad a ciudad.");
      }

      handleCloseDelete();
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa de ciudad a ciudad eliminada correctamente.",
      });
      await loadRatesCity({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text:
          error.message || "No se pudo eliminar la tarifa de ciudad a ciudad.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 960,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ color: "#fff !important" }}
                  >
                    CREAR TARIFA CLIENTE PARA CIUDAD A CIUDAD
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setCityCreateOpen(true)}
                    sx={{
                      bgcolor: "#fff",
                      color: "#165c53",
                      fontWeight: 800,
                      "&:hover": {
                        bgcolor: "#e8f3ef",
                      },
                    }}
                  >
                    Crear Ciudad
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Stack spacing={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      width: "100%",
                      maxWidth: 560,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <TarifaClientCityForm
                      form={form}
                      onChange={setForm}
                      onSubmit={handleCreate}
                      submitLabel="Crear"
                      loadingLabel="Creando..."
                      cityOptions={clientCities}
                      loadingCityOptions={loadingClientCities}
                      disabled={saving}
                    />
                  </Paper>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      overflowX: "auto",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: "#f4faf7",
                            "& th": {
                              fontWeight: 800,
                              color: "#12483f",
                            },
                          }}
                        >
                          <TableCell>Origen</TableCell>
                          <TableCell>Destino</TableCell>
                          <TableCell>Precio</TableCell>
                          <TableCell>Precio Km</TableCell>
                          <TableCell align="right">Opciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                                <CircularProgress />
                                <Typography color="text.secondary">
                                  Cargando tarifas...
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ) : records.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography sx={{ py: 3 }} color="text.secondary">
                                No hay tarifas registradas.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          records.map((record) => (
                            <TableRow key={record.id} hover>
                              <TableCell>{getRecordOriginName(record)}</TableCell>
                              <TableCell>{getRecordDestinationName(record)}</TableCell>
                              <TableCell>{formatPrice(record.price)}</TableCell>
                              <TableCell>
                                {formatPrice(record.priceKm)}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  aria-label="Opciones"
                                  onClick={(event) =>
                                    handleOpenMenu(event, record)
                                  }
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Box>
            </Paper>
          </Stack>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleOpenEdit}>Editar</MenuItem>
            <MenuItem onClick={handleOpenDelete}>Eliminar</MenuItem>
          </Menu>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={editOpen}
            onClose={handleCloseEdit}
          >
            <DialogTitle>Editar tarifa de ciudad a ciudad</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <TarifaClientCityForm
                  form={editForm}
                  onChange={setEditForm}
                  onSubmit={handleUpdate}
                  submitLabel="actualizar"
                  loadingLabel="actualizando..."
                  cityOptions={clientCities}
                  loadingCityOptions={loadingClientCities}
                  disabled={saving}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit}>cancelar</Button>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={cityCreateOpen}
            onClose={() => setCityCreateOpen(false)}
          >
            <DialogContent>
              <TarifaClientCityCreate
                onCancel={() => setCityCreateOpen(false)}
                onCreated={loadClientCities}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={deleteOpen} onClose={handleCloseDelete}>
            <DialogTitle>
              Deseas Eliminar esta Tarifa de Ciudad a ciudad?
            </DialogTitle>
            <DialogActions>
              <Button onClick={handleCloseDelete} disabled={deleting}>
                cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "eliminando..." : "aceptar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};

export default TarifaClientCityCreatePage;
